import ProductColors from './ProductColors/ProductColors';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import React from 'react';
import { styled } from 'linaria/react';
import MaxWidth from '../Layout/MaxWidth';
import AddToCartForm from './AddToCart/AddToCartForm';
import ImageContainer from './Images/ImageContainer';
import { ProductSection } from './ProductInfo';
import ProductInfoAccordion from './ProductInfoAccordion';
import ProductPageLoadingState from './ProductPageLoadingState';
import { RelatedProducts } from './RelatedProducts';
import { Container } from './styledComponents';
import { ProductMeta } from './ProductMeta';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import loadable from '@loadable/component';
import Spinner from '../ui/Spinner';
import { theme } from '../Theme';
import DynamicCategoryContent from '../ui/DynamicCategoryContent';
import CustomFieldsProducts from './CustomFieldsProducts';
import LipscoreWidget from '../Lipscore/LipscoreWidget';
import { Above, Below } from '@jetshop/ui/Breakpoints';

const LoadablePackageProduct = loadable(
  () => import('./PackageProduct/PackageProduct'),
  {
    fallback: <Spinner />
  }
);

const ProductContainer = styled('div')`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding-top: 1rem;
  ${theme.below.lg} {
    padding-top: 0;
  }

  .breadcrumbs {
    ${theme.below.lg} {
      margin: 0 0 5px 0;
      justify-content: center;
    }
  }

  .product-container {
    ${theme.below.lg} {
      padding: 0;
    }
  }

  header {
    h1 {
      font-size: 28px;
      line-height: 1.25;
      + h2 {
        font-size: 16px;
        font-weight: 600;
      }
    }
    margin-bottom: 24px;
    ${theme.below.lg} {
      margin-bottom: 10px;
      + div {
        margin-bottom: 10px;
      }
    }
  }
  .customer-comment-input-wrapper {
    width: 100%;
    .customer-comment-input {
      height: 44px;
    }
    .status-wrapper {
      top: -39px;
    }
  }
  .quantity-input-wrapper {
    margin-bottom: 0rem;
    margin-right: 10px;
    width: 54px;
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    input[type='number'] {
      border-radius: 4px;
      -moz-appearance: textfield; /* Firefox */
    }
    input {
      width: 54px;
      height: 54px;
      text-align: center;
      padding: 0px;
      margin-bottom: 0px;
    }
    label {
      text-align: center;
    }
  }
  .short-description {
    line-height: 1.5;
    font-size: 16px;
    margin-bottom: 24px;
    color: ${theme.colors.grey};
    p {
      margin-bottom: 1rem;
    }
  }
  .delivery-list {
    display: flex;
    justify-content: flex-start;
    margin-top: 10px;
    margin-bottom: 30px;
    li {
      display: flex;
      align-items: center;
      font-size: 12px;
      margin-right: 20px;
      margin-bottom: 16px;
      svg {
        margin-right: 5px;
        width: 16px;
        height: 16px;
      }
    }
  }
  .product-details {
    padding: 0 20px;
    ${theme.above.md} {
      align-items: flex-start;
    }

    .product-details-top {
      display: flex;
      width: 100%;
      align-items: flex-start;
      justify-content: space-between;
      font-size: 11px;
      margin-bottom: 30px;
      position: relative;
      ${theme.below.lg} {
        margin-bottom: 20px;
      }

      .product-brand-logo {
        position: absolute;
        right: 0;
        top: -20px;
        ${theme.below.lg} {
          top: -10px;
        }
        img {
          max-width: 100px;
          ${theme.below.lg} {
            max-width: 70px;
          }
        }
      }
    }

    .price,
    .new-price,
    .old-price {
      font-weight: 600;
      display: inline-block;
    }
    .price,
    .new-price {
      font-size: 1.5rem;
    }
    .new-price {
      margin-right: 8px;
      color: ${theme.colors.red};
    }
    .old-price {
      color: ${theme.colors.mediumgrey};
    }

    #lipscore-rating {
      margin-top: 10px;
    }
  }
`;

const ProductUsp = styled('div')`
  margin: 10px 0 30px 0;
  font-size: 14px;
  ul {
    li {
      &:before {
        content: '';
        height: 15px;
        width: 15px;
        background-image: url(/Check.svg);
        background-repeat: no-repeat;
        background-size: 100%;
        display: inline-block;
        vertical-align: middle;
        margin-right: 10px;
        margin-top: 2px;
      }
    }
  }
`;

const ReviewListContainer = styled('div')`
  display: block;
  border-top: 1px solid #e8e8e8;
  margin: 30px 0 0 0;
  ${theme.below.lg} {
    padding: 0 10px;
    border-top: none;
  }
`;

const Product = ({ result: { data, loading }, product }) => {
  const initialVariant = useVariantFromUrl();
  const variantHandler = useProductVariants(product, { initialVariant });

  const {
    selectedVariant: selectedVariation,
    getMissingOptions
  } = variantHandler;

  if (!product)
    return (
      <ProductContainer>
        <ProductPageLoadingState />
      </ProductContainer>
    );

  const images = product.images ?? [];
  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);
  const relatedProducts = product.relatedProducts;
  const colorName = product?.customFields?.find(({ key }) => key === 'basfarg');
  const brand = product?.customFields?.find(({ key }) => key === 'varumerke');
  const customFieldsProducts = product?.customFields?.find(
    ({ key }) => key === 'related'
  );
  const videoUrl = product?.customFields?.find(({ key }) => key === 'vimeourl')
    ?.stringValue;

  return (
    <ProductContainer>
      <MaxWidth>
        <Breadcrumbs
          className="breadcrumbs"
          parents={parents}
          breadcrumbs={product?.primaryRoute?.breadcrumbs}
        />
      </MaxWidth>
      <Container className="product-container">
        <ProductMeta {...{ product, selectedVariation }} />
        <ProductSection className="image-section">
          {images && (
            <ImageContainer
              video={videoUrl}
              images={images}
              variant={selectedVariation}
              badges={product.badges}
              product={product}
            />
          )}
          <Above breakpoint="lg">
            {matches =>
              matches && (
                <ReviewListContainer>
                  <LipscoreWidget type="review-list" product={product} />
                </ReviewListContainer>
              )
            }
          </Above>
        </ProductSection>
        <ProductSection>
          <div className="product-details">
            <div className="product-details-top">
              <div>
                <span>Art. nr: {product.articleNumber}</span>
              </div>
              <div className="product-brand-logo">
                {brand?.listValues?.length > 0 ? (
                  <img
                    src={
                      '//sportmann.no/pub_docs/files/logotyper/' +
                      brand.listValues[0]
                        .toLowerCase()
                        .replace('ø', 'o')
                        .replace('ö', 'o')
                        .replace('å', 'a')
                        .replace('ä', 'a')
                        .replace('æ', 'a')
                        .replace(/ /g, '-')
                        .replace(/[^A-Za-z-]/g, '')
                        .replace('--', '-') +
                      '.png'
                    }
                    alt="Product logo"
                    onError={e => {
                      e.target.style = 'display: none';
                    }}
                  />
                ) : null}
              </div>
            </div>
            <header>
              <h1 data-testid="product-title">
                {product.name || 'Loading...'}
              </h1>
              <LipscoreWidget type="rating" product={product} />
              <h2>{product.subName}</h2>
            </header>
            <ProductColors
              colorName={colorName?.stringValue ? colorName.stringValue : false}
              mainProductId={product.id}
              productArtNo={product.articleNumber}
            />

            {product?.isPackage ? (
              <LoadablePackageProduct product={product} />
            ) : (
              <AddToCartForm
                product={product}
                variant={selectedVariation}
                getMissingOptions={getMissingOptions}
                variantHandler={variantHandler}
              />
            )}
            <ProductUsp>
              <DynamicCategoryContent id={162} />
            </ProductUsp>
            <ProductInfoAccordion product={product} />
          </div>
        </ProductSection>
      </Container>
      <Below breakpoint="lg">
        {matches =>
          matches && (
            <ReviewListContainer>
              <LipscoreWidget type="review-list" product={product} />
            </ReviewListContainer>
          )
        }
      </Below>
      {relatedProducts && <RelatedProducts {...{ relatedProducts, loading }} />}
      {customFieldsProducts?.listValues?.length > 0 && (
        <CustomFieldsProducts products={customFieldsProducts.listValues} />
      )}
    </ProductContainer>
  );
};

export default Product;
